import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {FormattedMessage, useIntl} from 'react-intl';

import {IStore} from 'redux/interface';
import {
  createTrainingDialogue,
  getTrainingDialogues,
  getTranslations,
  removeTrainingDialogue,
  trainingsAsyncActions
} from 'redux/pageAsyncActions';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {Pages, TranslationKeys} from 'utils/constants/routeConstants';

import {ReactComponent as ChatIcon} from 'assets/image/menu-auth/chat.svg';
import {ButtonVariant, GritxButton} from '@wholesalechange/chatcomponent';
import NotFoundInfo from '../../components/not-found-info';
import NotFoundImg from '../../assets/image/common/notFound.png';
import Loader from '../../components/loader';
import {useNavigate} from 'react-router-dom';
import {IRadioInput} from '../../components/input-radio/interfaces';
import InputRadio from '../../components/input-radio';
import {clearTrainingDialogue} from '../../redux/trainings/actions';
import TrainingsCardSection from '../../components/trainings-card-section';
import {ITrainingDialogue} from '../../redux/trainings/interfaces';

import './styles.scss';

export const Trainings: React.FunctionComponent = () => {
  const {
    trainingDialogues,
    trainingDialogue
  } = useSelector((state: IStore) => state.trainings);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const {
    settings
  } = useSelector((state: IStore) => state.auth);
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sorted, setSorted] = useState<ITrainingDialogue[]>([]);
  const [sort, setSort] = useState<string>('new');
  const [afterCreate, setAfterCreate] = useState<boolean>(false);

  useEffect(() => {
    dispatch(clearTrainingDialogue());
  }, []);

  const sortRadioInputs: IRadioInput[] = [
    {
      id: 0,
      name: 'new',
      label: intl.formatMessage({
        id: 'gritx.search.sort.new',
        defaultMessage: 'Newest'
      }),
      value: 'new'
    },
    {
      id: 1,
      name: 'old',
      label: intl.formatMessage({
        id: 'gritx.search.sort.old',
        defaultMessage: 'Oldest'
      }),
      value: 'old'
    }];

  function getTime(date?: Date) {
    return date != null ? new Date(date).getTime() : 0;
  }

  function getSortedDialogues(sortMethod: string) {
    const dialoguesCopy = trainingDialogues.slice(0);

    if (sortMethod === 'old') {
      return dialoguesCopy.sort((a, b) => getTime(a.dialogUserDate) - getTime(b.dialogUserDate));
    }

    return dialoguesCopy.sort((a, b) => getTime(b.dialogUserDate) - getTime(a.dialogUserDate));
  }

  useEffect(() => {
    if (trainingDialogues) {
      setSorted(getSortedDialogues(sort));
    }
  }, [trainingDialogues, sort]);

  const translationKey = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Trainings.toLowerCase());

    return page?.translationKey || TranslationKeys.Trainings;
  }, [settings]);

  useGetTranslations({translationKey});

  const title = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Trainings.toLowerCase());

    return page?.title || Pages.Trainings;
  }, [settings]);

  useEffect(() => {
    if (afterCreate && trainingDialogue) {
      navigate(`/trainings/${trainingDialogue.dialogId}`);
      setAfterCreate(false);
    }
  }, [trainingDialogue]);

  useEffect(() => {
    dispatch(trainingsAsyncActions[getTrainingDialogues]());
  }, []);

  function handleOpenDialogue(dialogue: ITrainingDialogue) {
    navigate(`/trainings/${dialogue.dialogId}`);
  }

  function onCreateTrainingDialogue() {
    const date = new Date();
    const dateStr = intl.formatDate(date, {
      year: 'numeric',
      month: 'long',
      hour: 'numeric',
      minute: 'numeric',
      day: '2-digit'
    });

    dispatch(trainingsAsyncActions[createTrainingDialogue]({
      dialogId: 0,
      dialogName: `Training of ${dateStr}`,
      dialogUserDate: date,
      percentage: 0
    }));
    setAfterCreate(true);
  }

  function handleChangeSort(value: string) {
    setSort(value);
  }

  function renderDialogueCards() {
    return <div className="trainings__cards">
      {sorted.length === 0 ? <NotFoundInfo
        title={intl.formatMessage({
          id: 'gritx.trainings.sectionCards.notFound',
          defaultMessage: 'Sorry, but nothing can be found.'
        })}
        image={NotFoundImg}
      /> : <TrainingsCardSection
        showLoader={loading.includes(getTrainingDialogues)}
        cards={sorted}
        onClickCard={handleOpenDialogue}
      />}
    </div>;
  }

  return <section className={'trainings container'}>
    {
      !loading.includes(getTranslations) && <>
        <h1 className="trainings__title">
          <ChatIcon className="trainings__icon"/>
          <FormattedMessage
            id={`gritx.common.navigation.${title}`}
            defaultMessage={title}
          />
        </h1>
        <div className="trainings__header">
          <div className="trainings__header-sort">
            <InputRadio inputs={sortRadioInputs} value={sort} onChange={handleChangeSort} label={intl.formatMessage({
              id: 'gritx.trainings.sort.title',
              defaultMessage: 'Sort by:'
            })}/>
          </div>
          <GritxButton
            title={intl.formatMessage({
              id: 'gritx.trainings.create',
              defaultMessage: 'Create'
            })}
            className={'trainings__button'}
            variant={ButtonVariant.Primary}
            onClick={onCreateTrainingDialogue}
          />
        </div>
        {
          loading.includes(getTrainingDialogues) || loading.includes(removeTrainingDialogue)
          || loading.includes(createTrainingDialogue) ? <Loader/> : renderDialogueCards()
        }
      </>
    }
  </section>;
};
